export type FeatureId =
  | 'healthScore'
  | 'valuation'
  | 'arpu'
  | 'customerChurnRate'
  | 'revenueChurnRate'
  | 'mrrBreakdown'
  | 'nrrHighlight'
  | 'growthEfficiency'
  | 'collectionsBilling'
  | 'pricingDiscounts'
  | 'revenueMovement'
  | 'monthlyInsights'
  | 'extendedTimeRange'
  | 'publicProfile'
  | 'monthlyReports';

export type PlanTier = 'free' | 'pro' | 'enterprise';

export type FeatureAccessMap = Record<FeatureId, PlanTier>;

/** Default matrix — used when backend config is unavailable */
export const DEFAULT_FEATURE_ACCESS: FeatureAccessMap = {
  healthScore: 'pro',
  valuation: 'pro',
  arpu: 'pro',
  customerChurnRate: 'pro',
  revenueChurnRate: 'pro',
  mrrBreakdown: 'pro',
  nrrHighlight: 'pro',
  growthEfficiency: 'pro',
  collectionsBilling: 'pro',
  pricingDiscounts: 'pro',
  revenueMovement: 'pro',
  monthlyInsights: 'pro',
  extendedTimeRange: 'pro',
  publicProfile: 'pro',
  monthlyReports: 'pro',
};

const PLAN_RANK: Record<PlanTier, number> = { free: 0, pro: 1, enterprise: 2 };

export function planMeetsRequirement(userPlan: PlanTier, minPlan: PlanTier): boolean {
  return PLAN_RANK[userPlan] >= PLAN_RANK[minPlan];
}

/**
 * Validate and merge server config with defaults.
 * Input is raw wire data — may contain unknown keys or invalid values.
 * Only known FeatureId keys with valid PlanTier values are accepted.
 * Missing or invalid entries fall back to DEFAULT_FEATURE_ACCESS.
 */
export function resolveFeatureAccess(
  serverAccess: Partial<Record<string, string>> | undefined,
): FeatureAccessMap {
  if (!serverAccess) return { ...DEFAULT_FEATURE_ACCESS };
  const result = { ...DEFAULT_FEATURE_ACCESS };
  const validTiers = new Set<string>(['free', 'pro', 'enterprise']);
  for (const key of Object.keys(DEFAULT_FEATURE_ACCESS) as FeatureId[]) {
    const val = serverAccess[key];
    if (val && validTiers.has(val)) {
      result[key] = val as PlanTier;
    }
  }
  return result;
}
